import { Command } from 'commander';
import fs from 'fs-extra';
import path from 'path';
import pc from 'picocolors';
import { withErrorHandling } from '../utils/error-handler';
import { hasPermission, getFullWorkflowPermissions } from '../utils/claude-permissions';

export function createCheckPermissionsCommand(): Command {
  const command = new Command('check-permissions');
  
  command
    .description('Check Claude Code permissions for workflow-cli')
    .alias('perms')
    .option('--fix', 'Automatically fix missing permissions')
    .action(withErrorHandling(async (options) => {
      console.log(pc.bold('\n🔍 Checking Claude Code Permissions\n'));
      
      const projectRoot = process.cwd();
      const settingsPath = path.join(projectRoot, '.claude/settings.local.json');
      
      // 检查设置文件是否存在
      if (!await fs.pathExists(settingsPath)) {
        console.log(pc.red('❌ No .claude/settings.local.json found'));
        console.log(pc.dim('Run "workflow init --claude" to set up Claude Code integration'));
        return;
      }
      
      // 读取当前设置
      let settings: any = {};
      try {
        settings = await fs.readJson(settingsPath);
      } catch (e) {
        console.log(pc.red('❌ Failed to read settings file'));
        return;
      }
      
      // 获取需要的权限
      const requiredPermissions = getFullWorkflowPermissions();
      const currentPermissions = settings.permissions || { allow: [], deny: [] };
      
      // 检查每个权限
      const missingPermissions: string[] = [];
      const presentPermissions: string[] = [];
      
      console.log(pc.bold('Required Permissions:'));
      requiredPermissions.forEach(perm => {
        if (hasPermission(currentPermissions, perm)) {
          presentPermissions.push(perm);
          console.log(pc.green(`✅ ${perm}`));
        } else {
          missingPermissions.push(perm);
          console.log(pc.red(`❌ ${perm}`));
        }
      });
      
      // 汇总结果
      console.log(pc.bold('\n📊 Summary:'));
      console.log(`Total required: ${requiredPermissions.length}`);
      console.log(pc.green(`Present: ${presentPermissions.length}`));
      console.log(pc.red(`Missing: ${missingPermissions.length}`));
      
      // 修复权限
      if (missingPermissions.length > 0 && options.fix) {
        console.log(pc.bold('\n🔧 Fixing permissions...'));
        
        // 添加缺失的权限
        if (!settings.permissions) {
          settings.permissions = { allow: [], deny: [] };
        }
        
        missingPermissions.forEach(perm => {
          if (!settings.permissions.allow.includes(perm)) {
            settings.permissions.allow.push(perm);
          }
        });
        
        // 排序权限列表
        settings.permissions.allow.sort();
        
        // 保存更新的设置
        await fs.writeJson(settingsPath, settings, { spaces: 2 });
        
        console.log(pc.green('✅ Permissions fixed!'));
        console.log(pc.dim('\nRestart Claude Code for changes to take effect.'));
      } else if (missingPermissions.length > 0) {
        console.log(pc.yellow('\n⚠️  Missing permissions detected'));
        console.log(pc.dim('Run with --fix to automatically add them'));
      } else {
        console.log(pc.green('\n✅ All permissions are correctly configured!'));
      }
      
      // 检查 Hooks
      console.log(pc.bold('\n🪝 Checking Hooks:'));
      
      const hasEditHook = settings.hooks?.PostToolUse?.some((h: any) => 
        h.matcher === '^(Edit|Write|MultiEdit)'
      );
      const hasTodoHook = settings.hooks?.PostToolUse?.some((h: any) => 
        h.matcher === '^TodoWrite'
      );
      const hasStartHook = settings.hooks?.Start?.length > 0;
      
      console.log(hasEditHook ? pc.green('✅ Edit hook configured') : pc.red('❌ Edit hook missing'));
      console.log(hasTodoHook ? pc.green('✅ Todo hook configured') : pc.red('❌ Todo hook missing'));
      console.log(hasStartHook ? pc.green('✅ Start hook configured') : pc.red('❌ Start hook missing'));
      
      if (!hasEditHook || !hasTodoHook || !hasStartHook) {
        console.log(pc.yellow('\n⚠️  Some hooks are missing'));
        console.log(pc.dim('Run "workflow orchestrate start" to configure hooks'));
      }
    }));
  
  return command;
}